import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X, Phone, GraduationCap, ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { SITE } from "../../lib/site";

const links = [
  { href: "#de-ce-noi", label: "De ce noi" },
  { href: "#cum-functioneaza", label: "Cum funcționează" },
  { href: "#galerie", label: "Galerie" },
  { href: "#testimoniale", label: "Testimoniale" },
  { href: "#intrebari", label: "Întrebări" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const url = (h) => (pathname === "/" ? h : `/${h}`);
  const [promo, setPromo] = useState(() => {
    try {
      const until = localStorage.getItem("avd_promo_until");
      return !until || Date.now() > Number(until);
    } catch {
      return true;
    }
  });

  const closePromo = () => {
    setPromo(false);
    try {
      localStorage.setItem("avd_promo_until", String(Date.now() + 24 * 60 * 60 * 1000));
    } catch {}
  };

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="navbar" className="fixed top-0 inset-x-0 z-50">
      {/* Promo bar */}
      <AnimatePresence initial={false}>
        {promo && (
          <motion.div
            key="promo"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="promo-bar"
            className="overflow-hidden bg-gradient-to-r from-[#0D47A1] via-[#1565C0] to-[#0D47A1] text-white"
          >
            <div className="max-w-7xl mx-auto pl-6 pr-11 lg:px-8 h-9 flex items-center justify-center gap-2.5 text-[13px] font-medium relative">
              <a href={url("#contact")} data-testid="promo-cta" className="group inline-flex items-center gap-2 min-w-0">
                <GraduationCap size={15} className="shrink-0" />
                <span className="hidden sm:inline truncate">20% reducere pentru elevi, studenți, pensionari și șomeri — înscrieri deschise</span>
                <span className="sm:hidden truncate">20% reducere • înscrieri</span>
              </a>
              <a
                href="#contact"
                data-testid="promo-claim-btn"
                className="animate-pulse-glow shrink-0 inline-flex items-center gap-1 bg-white text-[#0D47A1] rounded-full px-3 py-0.5 text-xs font-bold hover:bg-blue-50 active:scale-95 transition-all"
              >
                <span className="hidden sm:inline">Profită acum</span>
                <span className="sm:hidden">Profită</span>
                <ArrowRight size={12} />
              </a>
              <button
                onClick={closePromo}
                data-testid="promo-close"
                aria-label="Închide"
                className="absolute right-3 grid place-items-center w-6 h-6 rounded-full hover:bg-white/15 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav row */}
      <div className={`relative transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 py-3" : "bg-transparent py-5"}`}>
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          <a href={url("#top")} data-testid="logo" className="flex items-center gap-2">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#1565C0] text-white font-display font-bold text-lg">A</span>
            <span className="font-display font-bold text-lg tracking-tight text-slate-900">
              Auto Valy <span className="text-[#1565C0]">Drive</span>
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={url(l.href)}
                data-testid={`nav-${l.href.slice(1)}`}
                className="text-sm font-medium text-slate-600 hover:text-[#1565C0] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>

          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="navbar-call-btn"
            className="hidden lg:inline-flex items-center gap-2 bg-[#1565C0] text-white hover:bg-[#0D47A1] rounded-full px-6 py-2.5 text-sm font-semibold transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={16} /> Sună acum
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden grid place-items-center w-10 h-10 rounded-xl bg-slate-100 text-slate-900"
            aria-label="Meniu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        {/* Scroll progress line */}
        <motion.div
          style={{ scaleX }}
          data-testid="scroll-progress"
          className="absolute bottom-0 inset-x-0 h-[3px] origin-left bg-gradient-to-r from-[#1565C0] to-[#60A5FA]"
        />
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden mt-2 mx-4 glass-card rounded-2xl p-4 flex flex-col gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={url(l.href)}
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-xl text-slate-700 font-medium hover:bg-blue-50 hover:text-[#1565C0] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href={`tel:${SITE.phoneRaw}`}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1565C0] text-white rounded-full px-6 py-3 font-semibold"
          >
            <Phone size={16} /> Sună acum
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
